import { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleCheckBig, CheckCircle2, Calendar, Tag, UserRound } from 'lucide-react';
import { Card } from '@components/base/layout';
import { Text, Icon, InfoList } from '@components/base/display';
import type { InfoItemDescriptor } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Alert } from '@components/base/feedback';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  FieldConfig,
  SelectFieldOptions,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { taskService } from '../api/taskService';
import type {
  MotifOption,
  VeosTaskInfo,
  WorkflowExecutionError,
} from '../api/taskService';
import { WorkflowStepTracker } from './WorkflowStepTracker';
import type { WorkflowStepExecutionSummary } from './WorkflowStepTracker';
import { useClaimDataStore } from './claimDataStore';

interface CloseTaskCardProps {
  taskId: string;
  taskInfo: VeosTaskInfo | null;
  /**
   * Claim id (`ID_SIN`). Feeds the "Statut / motif" options
   * (`WS2_GS_SEL_MOTIF`) and is sent to the close workflow with the chosen
   * motif. Absent ⇒ no motif field (and no fetch) — the task still closes.
   */
  idSin?: string;
  /**
   * Tenant-scoped layout overrides, injected by the `withComponentRules` HOC
   * (do not pass by hand). With no rules authored this is `undefined` and the
   * render is the baseline. Two surfaces share it, keyed by stable keys: the
   * info {@link InfoList} rows built in {@link buildCloseTaskItems}
   * (`taskDate` / `taskType` / `assignee`, read from `items` ?? `fields`) and
   * the EntityForm field built in {@link buildCloseTaskFormConfig} (`motif`,
   * plus the `submit` action) — merged through `applyLayoutOverrides`.
   */
  _layoutOverrides?: LayoutOverrides;
}

// ---------------------------------------------------------------------------
// Pill color helpers (same logic as taskListConfig)
// ---------------------------------------------------------------------------

const PILL_COLORS = [
  { bg: '#3b82f6', text: '#ffffff' },
  { bg: '#ef4444', text: '#ffffff' },
  { bg: '#f97316', text: '#ffffff' },
  { bg: '#8b5cf6', text: '#ffffff' },
  { bg: '#14b8a6', text: '#ffffff' },
  { bg: '#ec4899', text: '#ffffff' },
  { bg: '#eab308', text: '#ffffff' },
  { bg: '#06b6d4', text: '#ffffff' },
  { bg: '#10b981', text: '#ffffff' },
  { bg: '#6366f1', text: '#ffffff' },
  { bg: '#a855f7', text: '#ffffff' },
  { bg: '#f43f5e', text: '#ffffff' },
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = Math.trunc((hash << 5) - hash + (str.codePointAt(i) ?? 0));
  }
  return Math.abs(hash);
}

// Unreachable fallback (modulo always yields a valid index) — keeps the return
// type non-undefined under noUncheckedIndexedAccess.
const FALLBACK_PILL_COLOR = { bg: '#ef4444', text: '#ffffff' };

function getPillColor(label: string) {
  return PILL_COLORS[hashString(label) % PILL_COLORS.length] ?? FALLBACK_PILL_COLOR;
}

function renderTypePill(label: string) {
  const color = getPillColor(label);
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
      style={{ backgroundColor: color.bg, color: color.text }}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Date formatting helper
// ---------------------------------------------------------------------------

const frenchDateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function formatFrenchDate(raw: string | number): string {
  // ERP may return a numeric timestamp or a "DD/MM/YYYY" string
  if (typeof raw === 'number') {
    const date = new Date(raw);
    return isNaN(date.getTime()) ? String(raw) : frenchDateFormatter.format(date);
  }

  // Try parsing "DD/MM/YYYY" or "DD/MM/YYYY HH:mm"
  const datePart = raw.split(' ')[0] ?? raw;
  const parts = datePart.split('/');
  if (parts.length === 3) {
    const date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
    if (!isNaN(date.getTime())) return frenchDateFormatter.format(date);
  }

  return raw;
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

function capitalize(s: string): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatAssigneeName(info: VeosTaskInfo): string {
  return [info.PRENOM_PER, info.NOM_PER]
    .filter(Boolean)
    .map(capitalize)
    .join(' ');
}

function getTypeLabel(info: VeosTaskInfo): string {
  if (info.ECRAN?.toUpperCase() === 'AUTRE') return 'Action libre';
  return info.CLASSEMENT_LIB ?? '';
}

// ---------------------------------------------------------------------------
// Declarative descriptor for the read-only info rows
//
// The three labelled, tenant-facing rows (Date / Type / Assignée à) are
// rendered from this ordered descriptor with stable keys = their VEOS source
// identifiers (`taskDate` / `taskType` / `assignee`). Keys are the contract a
// rule targets; the French labels are display-only and overridable. The
// value-presence conditional is encoded here — a row whose source value is
// empty is *omitted from the descriptor*, so it is absent regardless of any
// override (an override `visible: true` can never force-show a value-less row,
// matching the pre-migration behaviour). The value itself is rendered the same
// way it was hand-laid: the type renders as the hash-coloured pill via
// `renderTypePill`; date and assignee render as plain text.
// ---------------------------------------------------------------------------

function buildCloseTaskItems(taskInfo: VeosTaskInfo | null): InfoItemDescriptor[] {
  if (!taskInfo) return [];

  const items: InfoItemDescriptor[] = [];

  const taskDate = taskInfo.DATE_DEBUT;
  if (taskDate) {
    items.push({
      key: 'taskDate',
      label: 'Date de la tâche',
      order: 1,
      icon: Calendar,
      value: formatFrenchDate(taskDate),
    });
  }

  const taskTypeLabel = getTypeLabel(taskInfo);
  if (taskTypeLabel) {
    items.push({
      key: 'taskType',
      label: 'Type de tâche',
      order: 2,
      icon: Tag,
      value: renderTypePill(taskTypeLabel),
    });
  }

  const assigneeName = formatAssigneeName(taskInfo);
  if (assigneeName) {
    items.push({
      key: 'assignee',
      label: 'Assignée à',
      order: 3,
      icon: UserRound,
      value: assigneeName,
    });
  }

  return items;
}

// ---------------------------------------------------------------------------
// Declarative form config for the "Statut / motif" select
//
// A single optional EntityForm field with the stable key `motif` (the contract
// a layout rule targets — rename / hide / tooltip / placeholder); its label,
// placeholder and tooltip live here, never in JSX. The value is the selected
// `CD_MOT`. The close button stays bespoke but reads its label / visibility
// from `actions.submit`, like the other migrated EntityForm cards.
// ---------------------------------------------------------------------------

const CLOSE_TASK_COMPONENT_ID = 'claimflow.closeTaskCard';
const CLOSE_TASK_TITLE = 'Clôturer la tâche';

/** Stable field key of the "Statut / motif" select. */
const MOTIF_FIELD_KEY = 'motif';

/**
 * Runtime-only texts of the motif select. They are NOT part of the published
 * base config: they temporarily replace the configured placeholder / helper
 * text while the options load, or once their fetch has failed.
 */
const MOTIF_LOAD_TEXT = {
  loadingPlaceholder: 'Chargement des statuts / motifs…',
  loadError:
    'Impossible de charger les statuts / motifs — la tâche peut être clôturée sans motif.',
} as const;

function buildCloseTaskFormConfig(motifs: MotifOption[]): EntityFormConfig {
  return {
    id: 'close-task',
    endpoint: {
      erpEndpoint: '/claimflow/workflows/claimflow-close-task/execute',
      method: 'POST',
    },
    fields: {
      [MOTIF_FIELD_KEY]: {
        type: 'select',
        label: 'Statut / motif',
        order: 1,
        tooltip:
          'Facultatif — le statut / motif choisi est enregistré sur le dossier à la clôture de la tâche.',
        options: {
          items: motifs.map((m) => ({ value: m.code, label: m.label })),
          searchable: true,
          placeholder: 'Sélectionnez un statut / motif',
        } as SelectFieldOptions,
      },
    },
    actions: {
      submit: { visible: true, label: CLOSE_TASK_TITLE },
      cancel: { visible: false },
      reset: { visible: false },
    },
    layout: { columns: 1 },
  };
}

type MotifLoadState = 'loading' | 'ready' | 'error';

/**
 * Overlay the runtime state of the motif select on the (override-merged)
 * config: hidden when the card has no claim id, disabled with a loading
 * placeholder while the options load, disabled with an explanatory helper text
 * when their fetch failed (non-blocking — the task can still be closed).
 */
function withMotifRuntimeState(
  config: EntityFormConfig,
  hasClaim: boolean,
  loadState: MotifLoadState,
): EntityFormConfig {
  const field = config.fields[MOTIF_FIELD_KEY];
  if (!field || (hasClaim && loadState === 'ready')) return config;

  let next: FieldConfig;
  if (!hasClaim) {
    next = { ...field, visible: false };
  } else if (loadState === 'loading') {
    next = {
      ...field,
      disabled: true,
      options: {
        ...(field.options as SelectFieldOptions | undefined),
        placeholder: MOTIF_LOAD_TEXT.loadingPlaceholder,
      } as SelectFieldOptions,
    };
  } else {
    next = { ...field, disabled: true, helperText: MOTIF_LOAD_TEXT.loadError };
  }
  return { ...config, fields: { ...config.fields, [MOTIF_FIELD_KEY]: next } };
}

/** The selected `CD_MOT` from the built payload, `undefined` when none. */
function readMotif(payload: Record<string, unknown> | FormData): string | undefined {
  const raw =
    payload instanceof FormData
      ? payload.get(MOTIF_FIELD_KEY)
      : payload[MOTIF_FIELD_KEY];
  return typeof raw === 'string' && raw !== '' ? raw : undefined;
}

/** Step trail of a failed close workflow run (see `WorkflowExecutionError`). */
function readStepExecutions(error: unknown): WorkflowStepExecutionSummary[] {
  if (!(error instanceof Error) || !('stepExecutions' in error)) return [];
  const steps = (error as WorkflowExecutionError).stepExecutions;
  return Array.isArray(steps) ? steps : [];
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CloseTaskCard({
  taskId,
  taskInfo,
  idSin,
  _layoutOverrides,
}: Readonly<CloseTaskCardProps>) {
  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  // This task closed from the « Tâches » card's inline "Clôturer" (22862
  // follow-up): enter the same "(Tâche clôturée)" state as after our own
  // submit — no API call (the close already happened), idempotent (only while
  // not submitted yet), and a signal for another task id is ignored. Adjusted
  // during render, so the closed state paints with the signal.
  const lastClosedTask = useClaimDataStore((s) => s.lastClosedTask);
  if (!submitted && lastClosedTask?.idEvent === taskId) {
    setSubmitted(true);
  }

  const [error, setError] = useState<string | null>(null);
  const [failedSteps, setFailedSteps] = useState<WorkflowStepExecutionSummary[]>([]);

  const [motifs, setMotifs] = useState<MotifOption[]>([]);
  const [motifLoadState, setMotifLoadState] = useState<MotifLoadState>('loading');

  useEffect(() => {
    if (!idSin) return undefined;
    let cancelled = false;

    // Async loader so the setState calls are not synchronous statements in
    // the effect body (react-hooks/set-state-in-effect). A failure is
    // non-blocking: the select is disabled and the task closes without motif.
    async function loadMotifs(claimId: string): Promise<void> {
      setMotifLoadState('loading');
      try {
        const options = await taskService.fetchMotifs(claimId);
        if (cancelled) return;
        setMotifs(options);
        setMotifLoadState('ready');
      } catch {
        if (cancelled) return;
        setMotifs([]);
        setMotifLoadState('error');
      }
    }

    void loadMotifs(idSin);

    return () => {
      cancelled = true;
    };
  }, [idSin]);

  const items = useMemo(
    () => buildCloseTaskItems(taskInfo),
    [taskInfo],
  );

  const baseFormConfig = useMemo(
    () => buildCloseTaskFormConfig(motifs),
    [motifs],
  );

  // One published config for both surfaces (info rows + motif field + submit
  // action), like `EmailView` — the editor lists every target of the card.
  usePublishLayoutConfig(CLOSE_TASK_COMPONENT_ID, {
    ...buildPublishedConfigFromInfoItems(items, CLOSE_TASK_TITLE),
    fields: baseFormConfig.fields,
    actions: baseFormConfig.actions,
  });

  const formConfig = useMemo(
    () =>
      withMotifRuntimeState(
        applyLayoutOverrides(baseFormConfig, _layoutOverrides),
        Boolean(idSin),
        motifLoadState,
      ),
    [baseFormConfig, _layoutOverrides, idSin, motifLoadState],
  );

  const mutationFn = useCallback(
    (payload: Record<string, unknown> | FormData) => {
      setError(null);
      setFailedSteps([]);
      return taskService.closeTask(taskId, { idSin, motif: readMotif(payload) });
    },
    [taskId, idSin],
  );

  const handleSuccess = useCallback(() => {
    setSubmitted(true);
    // The current task is now closed — the « Tâches » card re-fetches its
    // list so the row shows as done (mahhp 22862).
    useClaimDataStore.getState().bumpTasks();
  }, []);

  const handleError = useCallback((e: unknown) => {
    // Surface a visible error instead of failing silently — the user can
    // retry; a failed workflow run also shows its step trail.
    setError(e instanceof Error ? e.message : 'La clôture de la tâche a échoué.');
    setFailedSteps(readStepExecutions(e));
  }, []);

  const form = useEntityForm({
    config: formConfig,
    mutationFn,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  // The whole info block is hidden when no row has a value (parity with the
  // pre-migration `hasAnyInfo` gate). Computed from the built descriptor so it
  // stays in lockstep with the value-presence conditional inside the builder.
  const hasAnyInfo = items.length > 0;

  // `variant="inline"` restores the pre-migration single-line `label value`
  // look for the three read-only info rows.
  const infoList = hasAnyInfo && (
    <InfoList
      items={items}
      columns={1}
      variant="inline"
      layoutOverrides={_layoutOverrides}
    />
  );

  if (submitted) {
    return (
      <Card key="submitted">
        <Card.Header collapsible defaultExpanded={false}>
          <Icon icon={CheckCircle2} size="md" color="success" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle(CLOSE_TASK_TITLE, _layoutOverrides)}
          </Text>
          <Text as="span" size="sm" color="muted" className="ml-2">
            (Tâche clôturée)
          </Text>
        </Card.Header>
        <Card.Content>
          {infoList}
          <div className="pt-4">
            <Button
              variant="primary"
              disabled
              className="w-full opacity-60"
            >
              Tâche clôturée
            </Button>
          </div>
        </Card.Content>
      </Card>
    );
  }

  const hasVisibleField = Object.keys(formConfig.fields).some((key) =>
    form.isFieldVisible(key),
  );

  return (
    <Card key="open">
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        <Icon icon={CircleCheckBig} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle(CLOSE_TASK_TITLE, _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        {infoList}
        <EntityForm config={formConfig} form={form}>
          {hasVisibleField && (
            <div className="pt-4">
              <EntityForm.Fields />
            </div>
          )}
          {error && (
            <div className="pt-4">
              <Alert variant="error" title="La clôture a échoué">
                {error}
              </Alert>
            </div>
          )}
          {failedSteps.length > 0 && (
            <div className="pt-4">
              <Text size="sm" weight="semibold" className="mb-2">
                Détail de l&apos;exécution :
              </Text>
              <WorkflowStepTracker steps={failedSteps} />
            </div>
          )}
          {formConfig.actions?.submit?.visible !== false && (
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                loading={form.isSubmitting}
                disabled={form.isSubmitting}
                className="w-full"
              >
                {formConfig.actions?.submit?.label ?? CLOSE_TASK_TITLE}
              </Button>
            </div>
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}
